import { Box, Button as MuiButton, Paper } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { ServiceWorkerConfig } from "serviceWorkerConfig";

const logger = {
  log: (...args: any) => console.log(...args),
  warn: (...args: any) => console.warn(...args),
  error: (...args: any) => console.error(...args),
};

const originalUpdateWorker = ServiceWorkerConfig.onUpdate;

type OptionalUpdateDialogProps = {
  onUpdate: () => void;
};

const OptionalUpdateDialog = ({ onUpdate }: OptionalUpdateDialogProps) => {
  const handleClickUpdate = (e: React.MouseEvent) => {
    e.preventDefault();
    onUpdate();
  };

  return (
    <Paper
      sx={{
        position: "fixed",
        zIndex: 1,
        bottom: 32,
        right: 32,
        border: "solid 1px #EB5757",
        width: 353,
        height: 84,
        display: "flex",
        alignItems: "center",
        p: 1,
        padding: "20px",
        fontSize: "12px",
        justifyContent: "space-between",
        boxShadow: "none",
      }}
    >
      <Box>
        <Box sx={{ fontSize: "15px", fontWeight: "bold" }}>🔔 알림</Box>
        <Box>
          새로운 버전이 출시되었습니다. <br />
          업데이트 후 이용해주시기 바랍니다.
        </Box>
      </Box>
      <MuiButton
        sx={{
          width: 78,
          height: 28,
          backgroundColor: "#EB5757",
          color: "white",
          fontWeight: "bold",
          "&:hover": {
            backgroundColor: "#EB5757",
          },
        }}
        onClick={handleClickUpdate}
      >
        업데이트
      </MuiButton>
    </Paper>
  );
};

// 서비스워커가 새 버전(새 배포)을 감지하면 우측 하단에 업데이트 안내를 띄운다
export function Updater() {
  const [hasServiceWorkerUpdate, setHasServiceWorkerUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const reloadFunc = useRef<(() => void) | null>(null);

  const handleUpdate = useCallback(() => {
    if (reloadFunc.current) {
      logger.log("Executing update function");
      reloadFunc.current();
    } else {
      logger.warn("Update function is not available");
    }
  }, []);

  useEffect(() => {
    ServiceWorkerConfig.onUpdate = (registration) => {
      logger.log("Service worker update detected");

      if (!registration.waiting) {
        logger.warn("No waiting service worker found");
        return;
      }

      setHasServiceWorkerUpdate(true);

      reloadFunc.current = () => {
        setIsUpdating(true);
        setHasServiceWorkerUpdate(false);
        logger.log("Starting update process");

        try {
          originalUpdateWorker(registration);
        } catch (error) {
          logger.error("Error during update:", error);
          setIsUpdating(false);
          setHasServiceWorkerUpdate(true);
        }
      };
    };

    // 핸들러 복원
    return () => {
      ServiceWorkerConfig.onUpdate = originalUpdateWorker;
    };
  }, []);

  if (isUpdating || !hasServiceWorkerUpdate) {
    return null;
  }

  return <OptionalUpdateDialog onUpdate={handleUpdate} />;
}
